import MuiAlert from "@mui/material/Alert"
import DOMPurify from "isomorphic-dompurify"
import React, { FC, useId, useLayoutEffect, useRef, useState } from "react"

import { create } from "src/helpers/bem"
import { useTranslation } from "src/hooks/translation"
import { useLocale } from "src/states/locale"
import {
  setDismissedNoticeBanners,
  useDismissedNoticeBanners,
} from "src/states/noticeBanner"

import styles from "./NoticeBanner.module.scss"

const bem = create(styles, "NoticeBanner")

// CSS variable with the current banner height. The sticky header and the
// fixed sub headers use it to move down while the banner is visible.
export const NOTICE_BANNER_HEIGHT_VARIABLE = "--notice-banner-height"

// Uses the exact (fractional) height. A rounded value leaves a visible
// 1px gap or overlap on screens with display scaling, e.g. 125 %.
const setBannerHeight = (height: number) => {
  document.documentElement.style.setProperty(
    NOTICE_BANNER_HEIGHT_VARIABLE,
    `${height}px`,
  )
}

export type NoticeBannerContent = {
  title: string
  details: string
}

const readContent = (locale: "de" | "en"): NoticeBannerContent => {
  const title =
    locale === "en"
      ? process.env.NOTICE_BANNER_TITLE_EN
      : process.env.NOTICE_BANNER_TITLE_DE
  const details =
    locale === "en"
      ? process.env.NOTICE_BANNER_DETAILS_EN
      : process.env.NOTICE_BANNER_DETAILS_DE
  return { title: title?.trim() ?? "", details: details?.trim() ?? "" }
}

const hasContent = ({ title, details }: NoticeBannerContent) =>
  Boolean(title || details)

/**
 * Returns the global banner content (environments/notice-banner.json) for the
 * given locale. Falls back to the other language if only one is configured.
 */
export const getNoticeBannerContent = (
  locale?: string,
): NoticeBannerContent => {
  const de = readContent("de")
  const en = readContent("en")
  if (locale === "en") {
    return hasContent(en) ? en : de
  }
  return hasContent(de) ? de : en
}

/**
 * Identifies a banner text. Closing the banner stores this value, so a
 * changed title or changed details show the banner again.
 */
export const getNoticeBannerId = ({ title, details }: NoticeBannerContent) =>
  hasContent({ title, details }) ? `${title}\n${details}` : ""

/**
 * Returns the list of closed banners after closing the given one. Only the
 * currently configured banners are kept, so outdated entries are removed.
 */
export const addDismissedNoticeBanner = (dismissed: string[], id: string) => {
  const current = [
    getNoticeBannerId(getNoticeBannerContent("de")),
    getNoticeBannerId(getNoticeBannerContent("en")),
  ]
  return [
    ...dismissed.filter((item) => item !== id && current.includes(item)),
    id,
  ]
}

/**
 * Cleans the configured HTML and makes all links open in a new tab, so users
 * do not leave the portal. DOMPurify removes target attributes by default,
 * so they are added after sanitizing.
 */
const sanitize = (html: string) => {
  const fragment = DOMPurify.sanitize(html, { RETURN_DOM_FRAGMENT: true })
  fragment.querySelectorAll("a[href]").forEach((link) => {
    link.setAttribute("target", "_blank")
    link.setAttribute("rel", "noopener noreferrer")
  })
  const container = fragment.ownerDocument.createElement("div")
  container.appendChild(fragment)
  return { __html: container.innerHTML }
}
const toPlainText = (html: string) =>
  DOMPurify.sanitize(html, { ALLOWED_TAGS: [] }).trim()

export const NoticeBanner: FC = () => {
  const locale = useLocale()
  const { messages } = useTranslation()
  const m = messages?.components?.core?.noticeBanner
  const storedDismissed = useDismissedNoticeBanners()
  // Values saved in an older format are ignored.
  const dismissed = Array.isArray(storedDismissed) ? storedDismissed : []
  const content = getNoticeBannerContent(locale)
  const id = getNoticeBannerId(content)
  // The banner shows once per language: closing it only hides the current text.
  const isVisible = Boolean(id) && !dismissed.includes(id)
  const [isExpanded, setIsExpanded] = useState(false)
  const detailsId = useId()
  const ref = useRef<HTMLDivElement>(null)

  // Keeps the CSS variable in sync with the banner height, which changes when
  // the text wraps on smaller screens or the details are expanded. Resets it
  // to 0 when the banner is hidden.
  useLayoutEffect(() => {
    const element = ref.current
    if (!isVisible || !element) {
      setBannerHeight(0)
      return
    }

    setBannerHeight(element.getBoundingClientRect().height)
    if (typeof ResizeObserver === "undefined") {
      return () => setBannerHeight(0)
    }

    const observer = new ResizeObserver(() =>
      setBannerHeight(element.getBoundingClientRect().height),
    )
    observer.observe(element)
    return () => {
      observer.disconnect()
      setBannerHeight(0)
    }
  }, [isVisible])

  if (!isVisible) {
    return null
  }

  return (
    <div ref={ref} className={bem()}>
      <MuiAlert
        aria-label={toPlainText(content.title) || undefined}
        className={bem("alert")}
        closeText={m?.close}
        role="region"
        severity="info"
        variant="filled"
        onClose={() =>
          setDismissedNoticeBanners(addDismissedNoticeBanner(dismissed, id))
        }
      >
        {content.title && (
          <div
            className={bem("title")}
            dangerouslySetInnerHTML={sanitize(content.title)}
          />
        )}
        {content.details && (
          <>
            {(isExpanded || !content.title) && (
              <div
                className={bem("details")}
                dangerouslySetInnerHTML={sanitize(content.details)}
                id={detailsId}
              />
            )}
            {content.title && (
              <button
                aria-controls={detailsId}
                aria-expanded={isExpanded}
                className={bem("toggle")}
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? m?.showLess : m?.showMore}
              </button>
            )}
          </>
        )}
      </MuiAlert>
    </div>
  )
}
